import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Trash2, UserPlus, ShieldCheck, ShieldOff, Crown, PauseCircle } from "lucide-react";
import { SkeletonTable, Skeleton } from "@/components/ui/skeleton";

const OWNER_PROTECTED = (process.env.REACT_APP_OWNER_EMAIL || "").toLowerCase();

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      await api.post("/admin/users", { email: email.trim(), password, display_name: displayName });
      toast.success("Admin created");
      setOpen(false);
      setEmail(""); setPassword(""); setDisplayName("");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setActing(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setActing(true);
    try {
      await api.delete(`/admin/users/${u.uid}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setActing(false);
    }
  };

  const suspend = async (u) => {
    if (!window.confirm(`Toggle suspension for ${u.email}?`)) return;
    setActing(true);
    try {
      const { data } = await api.post(`/admin/users/${u.uid}/suspend`);
      toast.success(data.suspended ? "Account suspended" : "Account reactivated");
      await load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const promote = async (u) => {
    if (!window.confirm(`Promote ${u.email} to OWNER? They'll be able to manage other admins.`)) return;
    setActing(true);
    try {
      await api.post(`/admin/users/${u.uid}/promote`);
      toast.success("Promoted to owner");
      await load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline mb-2">User Management</p>
          <h1 className="font-serif text-4xl">Admins</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="admin-add-user-button">
          <UserPlus size={14} /> Add Admin
        </button>
      </div>

      {loading ? (
        <SkeletonTable rows={5} columns={3} />
      ) : (
        <div className="card-luxe overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left">
                <th className="px-5 py-3 overline">Email</th>
                <th className="px-5 py-3 overline">Role</th>
                <th className="px-5 py-3 overline">Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isProtected = u.email === OWNER_PROTECTED;
                const isOwner = u.role === "owner";
                const isMe = u.email === me?.email;
                return (
                  <tr key={u.uid} className="hover:bg-foreground/[0.02]">
                    <td className="px-5 py-4 text-foreground">{u.email} {isMe && <span className="text-faint text-xs ml-2">· you</span>} {u.suspended && <span className="text-yellow-500 text-xs ml-2">· suspended</span>}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] ${isOwner ? "text-foreground" : "text-muted"}`}>
                        {isOwner && <ShieldCheck size={12} />} {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-faint text-xs">{u.created_at?.slice(0, 10) || "—"}</td>
                    <td className="px-5 py-4 text-right">
                      {isProtected ? (
                        <span className="text-xs text-faint italic">Principal · protected</span>
                      ) : (
                        <div className="flex justify-end gap-3">
                          {!isOwner && (
                            <button onClick={() => promote(u)} className="text-xs text-foreground hover:underline inline-flex items-center gap-1" data-testid={`promote-${u.uid}`}>
                              <Crown size={12} /> Promote
                            </button>
                          )}
                          <button onClick={() => suspend(u)} className="text-xs text-yellow-500 hover:underline inline-flex items-center gap-1" data-testid={`suspend-${u.uid}`}>
                            <PauseCircle size={12} /> {u.suspended ? "Unsuspend" : "Suspend"}
                          </button>
                          <button onClick={() => remove(u)} className="text-xs text-red-400 hover:underline inline-flex items-center gap-1" data-testid={`delete-${u.uid}`}>
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-add-user-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setOpen(false)}></div>
          <form onSubmit={create} className="relative card-luxe p-8 max-w-md w-full">
            <p className="overline mb-2">New Admin</p>
            <h3 className="font-serif text-2xl mb-6">Add a team member</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="overline block mb-2">Email</label>
                <input type="email" required className="input-luxe" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-admin-email" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <input type="text" required minLength={6} className="input-luxe font-mono" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-admin-password" />
              </div>
              <div>
                <label className="overline block mb-2">Display Name (optional)</label>
                <input className="input-luxe" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="new-admin-display-name" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={acting} data-testid="new-admin-submit">
                {acting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
